import { useEffect, useState } from 'react';

import { getPipelineLeads } from 'redux/actions/leadsActions.js';
import selectors from 'selectors/selectors';
import { useSelector } from 'react-redux';
import { timeSinceFormat } from 'lib/dateUtilities.js';

const calculateLeadCountAndValue = (accumulator) => (all, item) => {
  if (!all[item.stage_id]) {
    all[item.stage_id] = 0;
  }
  all[item.stage_id] = all[item.stage_id] += accumulator === 1 ? 1 : item.lead_value;
  return all;
};

const useCirclePipelineGraph = (props) => {
  const pipelineId = props.id;
  const [loading, setIsLoading] = useState(false);
  const activePipelineGraphData = useSelector((state) => state.reporting.activePipelineGraphData);

  const serviceProviderId = useSelector(selectors.serviceProviderId);
  const serviceProviderPipelines = useSelector((state) => state.currentServiceProvider.pipelines);
  const filteredLocationId = useSelector(selectors.filteredLocationId);
  const filteredUserId = useSelector(selectors.filteredUserId);

  useEffect(() => {
    setIsLoading(true);
    getPipelineLeads(
      pipelineId,
      () => {
        setIsLoading(false);
      },
      'FETCH_ACTIVE_PIPELINE_GRAPH_DATA'
    );
  }, [serviceProviderId, filteredLocationId, filteredUserId, pipelineId]);

  const currentPipeline = serviceProviderPipelines.find((item) => item.id === pipelineId);

  const stages = currentPipeline.stages;

  let dataFormatted = activePipelineGraphData;
  // Filter by user if filter is selected
  dataFormatted = filteredUserId ? dataFormatted.filter((item) => ((item || {}).user || {}).id === filteredUserId) : dataFormatted;
  // Filter by location filter is selected
  dataFormatted = filteredLocationId ? dataFormatted.filter((item) => (item || {}).service_location_id === filteredLocationId) : dataFormatted;

  let tempServiceInterests = [];

  dataFormatted.forEach((item) => {
    const serviceInterests = item.service_interests;
    tempServiceInterests = [...tempServiceInterests, ...serviceInterests];
  });

  const totalLeadValueMap = tempServiceInterests.reduce((all, item) => {
    all[item.pipeline_lead_id] = all[item.id] ? (all[item.pipeline_lead_id] += parseInt(item.service_total_value)) : (all[item.pipeline_lead_id] = parseInt(item.service_total_value));
    return all;
  }, {});

  const leadWithCalculatedTotalValue = dataFormatted.map((item) => ({
    ...item,
    lead_value: totalLeadValueMap[item.id] ? totalLeadValueMap[item.id] : 0,
  }));

  const stageMetaMap = dataFormatted.reduce((all, pipelineLead) => {
    const { stage_id } = pipelineLead;
    if (!all[stage_id]) {
      all[stage_id] = [];
    }
    if (pipelineLead.meta.last_stage_update_date) {
      const lastStageChange = ((pipelineLead.meta || {}).last_stage_update_date || {}).value || pipelineLead.creation_datetime;
      all[stage_id].push(parseInt(timeSinceFormat(lastStageChange, 'PPpp').replace(/\D/g, '')));
    }
    return all;
  }, {});

  const stageMetaEntries = Object.entries(stageMetaMap);
  const averageTimeInStage = stageMetaEntries.reduce((all, item) => {
    const stageId = item[0];
    const data = item[1];
    if (!all[stageId]) {
      all[stageId] = undefined;
    }
    if ((data || []).length) {
      const average = data.reduce((a, b) => a + b) / data.length;
      all[stageId] = average.toFixed(0);
    }
    return all;
  }, {});

  const numberOfLeadsOverAverageTimeInStage = stageMetaEntries.reduce((all, item) => {
    const stageId = item[0];
    const data = item[1];
    if (!all[stageId] && stageId && (data || []).length) {
      all[stageId] = data.filter((item) => item > parseInt(averageTimeInStage[stageId])).length;
    }
    return all;
  }, {});

  const leadCountPerStage = leadWithCalculatedTotalValue.reduce(calculateLeadCountAndValue(1), {});

  const leadValuePerStage = leadWithCalculatedTotalValue.reduce(calculateLeadCountAndValue(0), {});

  const currentStagesModified = stages.map((item) => {
    const newValues = {
      ...item,
      lead_count: leadCountPerStage[item.id] ? leadCountPerStage[item.id] : 0,
      lead_value: leadValuePerStage[item.id] ? leadValuePerStage[item.id] : 0,
      averageDaysInStage: averageTimeInStage[item.id] ? averageTimeInStage[item.id] : undefined,
      numberOfLeadsOverAverageTimeInStage: numberOfLeadsOverAverageTimeInStage[item.id] ? numberOfLeadsOverAverageTimeInStage[item.id] : undefined,
    };
    return newValues;
  });

  return { data: { stages: currentStagesModified, pipeline_name: currentPipeline.pipeline_name }, loading };
};

export { useCirclePipelineGraph };
